import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Share2, Linkedin, Twitter, Link as LinkIcon, Check } from 'lucide-react';

/**
 * Compact share menu that pops LinkedIn + Twitter + copy-link actions.
 * The dropdown is rendered via a portal to escape any parent `overflow: hidden`
 * (e.g. cards with rounded, clipped corners).
 */
const ShareButton = ({ url, title, testId = 'episode-share' }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  // Position the portal menu right below the trigger button.
  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    setPos({ top: r.bottom + 8, left: r.left });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  const stop = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };
  const toggle = (e) => {
    stop(e);
    setOpen((v) => !v);
  };

  const shareLinkedIn = (e) => {
    stop(e);
    const u = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
    window.open(u, '_blank', 'noopener,noreferrer,width=680,height=560');
    setOpen(false);
  };

  const shareTwitter = (e) => {
    stop(e);
    const u = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`;
    window.open(u, '_blank', 'noopener,noreferrer,width=680,height=560');
    setOpen(false);
  };

  const copyLink = async (e) => {
    stop(e);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      // no-op
    }
  };

  const spring = { type: 'spring', stiffness: 700, damping: 24, mass: 0.6 };

  return (
    <>
      <motion.button
        ref={btnRef}
        type="button"
        onClick={toggle}
        data-testid={testId}
        aria-label="Share this episode"
        aria-expanded={open}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.92 }}
        transition={spring}
        className="neon-hover w-9 h-9 sm:w-10 sm:h-10 rounded-full glass border border-white/10 hover:border-[#EF5B5B]/60 flex items-center justify-center text-gray-200 hover:text-[#EF5B5B] backdrop-blur-md bg-black/40"
      >
        <Share2 className="w-4 h-4" />
      </motion.button>

      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              ref={menuRef}
              initial={{ opacity: 0, scale: 0.85, y: -6 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: -4 }}
              transition={spring}
              className="fixed z-[100] min-w-[190px] rounded-2xl border border-white/10 p-2 shadow-2xl"
              style={{
                top: pos.top,
                left: pos.left,
                background: 'rgba(15,15,17,0.95)',
                backdropFilter: 'blur(14px)',
                boxShadow: '0 20px 60px -20px rgba(239,91,91,0.55), 0 0 0 1px rgba(239,91,91,0.25)',
              }}
              data-testid={`${testId}-menu`}
              onClick={stop}
            >
              <motion.button
                type="button"
                onClick={shareLinkedIn}
                whileHover={{ x: 3 }}
                transition={spring}
                data-testid={`${testId}-linkedin`}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-100 hover:bg-white/5"
              >
                <Linkedin className="w-4 h-4 text-[#0A66C2]" /> LinkedIn
              </motion.button>
              <motion.button
                type="button"
                onClick={shareTwitter}
                whileHover={{ x: 3 }}
                transition={spring}
                data-testid={`${testId}-twitter`}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-100 hover:bg-white/5"
              >
                <Twitter className="w-4 h-4 text-sky-400" /> Twitter / X
              </motion.button>
              <motion.button
                type="button"
                onClick={copyLink}
                whileHover={{ x: 3 }}
                transition={spring}
                data-testid={`${testId}-copy`}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-100 hover:bg-white/5"
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 text-emerald-400" /> Copied!
                  </>
                ) : (
                  <>
                    <LinkIcon className="w-4 h-4 text-gray-400" /> Copy link
                  </>
                )}
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default ShareButton;
